<template>
    <v-container fluid>
        <div class="d-flex align-center justify-space-between mb-4">
            <h1 class="text-h5 ma-0">{{ $t('№') }} {{ req.record_id }} {{ req.type_name___ru }}</h1>

            <v-menu v-if="canShowActionsMenu" offset-y left>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" class="rounded-circle" color="gray" :disabled="uiDisabled || !isDocumentReady">
                        <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                </template>

                <v-list dense>
                    <v-list-item v-if="canMenuAssignToMe" @click="assignToMe">
                        <v-list-item-title>{{ $t('Назначить себе') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="canMenuAssignExecutor" @click="openAssignExecutor">
                        <v-list-item-title>{{ $t('Назначить исполнителя') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="canMenuReturn" @click="openReturnDialog">
                        <v-list-item-title>{{ $t('Вернуть заявку') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="canMenuAttachDocs" @click="openAttachDocs">
                        <v-list-item-title>{{ $t('Прикрепить документы к заявке') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="canMenuExecute" @click="openExecuteConfirm">
                        <v-list-item-title>{{ $t('Исполнить заявку') }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>

        <v-card-text v-if="req">
            <v-row>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Номер договора') }}:</strong> {{ req.object_contracts ? req.object_contracts[0].contract_number : '' }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Наименование организации') }}:</strong>
                    {{ req.object_contracts ? req.object_contracts[0].contractor_name___ru : '' }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Наименование объекта договора') }}:</strong>
                    {{ req.object_contracts ? req.object_contracts[0].object_name___ru : '' }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Адрес объекта') }}:</strong> {{ req.object_contracts ? req.object_contracts[0].address_full_name___ru : '' }}
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Контактный номер телефона') }}:</strong>
                    {{ getContactPhone(req) }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Контактное лицо') }}:</strong> {{ getContactName(req) }}
                </v-col>
                <v-col cols="12" md="3">
                    <strong>{{ $t('Дополнительная информация к заявке') }}:</strong>
                    <div class="req-addinfo">
                        {{ getAddInfo(req) }}
                    </div>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <div class="mt-1">
                        <strong>{{ $t('Исполнитель') }}: </strong>
                        <span v-if="executorLoading2">{{ $t('Загрузка...') }}</span>
                        <span v-else>{{ executorName2 }}</span>
                    </div>
                </v-col>
            </v-row>
        </v-card-text>

        <v-card-text v-else>
            <v-progress-circular indeterminate color="gray"></v-progress-circular>
            {{ $t('Загрузка...') }}
        </v-card-text>

        <v-card v-if="hasDocuments" outlined class="pa-4 mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t('Документы') }}
            </div>

            <v-row>
                <v-col
                    cols="12"
                    v-for="(doc, idx) in req.req_documents"
                    :key="doc.record_id || doc.attachment_link_rid || doc.attach_id || `${doc.attach_file_name}-${idx}`"
                >
                    <div class="d-flex align-center flex-wrap">
                        <v-icon size="18" class="mr-2" color="gray">mdi-file</v-icon>
                        <span class="doc-filename mr-2">{{ getDocDisplayName(doc) }}</span>

                        <v-btn icon class="mr-1" :title="$t('Просмотр')" @click="previewDoc(doc)">
                            <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                        <v-btn icon :title="$t('Скачать')" @click="downloadDoc(doc)">
                            <v-icon small>mdi-download</v-icon>
                        </v-btn>
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <v-card outlined class="pa-4 mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t('Заметки') }}
            </div>

            <v-btn
                v-if="canShowButComment"
                icon
                size="small"
                color="primary"
                class="note-fab rounded-circle"
                @click="openNoteDialog"
                :disabled="uiDisabled || !isDocumentReady"
            >
                <v-icon>mdi-plus</v-icon>
            </v-btn>

            <div v-if="req && getNoteFromInfo(req)" class="text-body-2">
                {{ getNoteFromInfo(req) }}
            </div>
        </v-card>

        <v-dialog v-model="dialogNote" max-width="640">
            <v-card class="note-dialog-card">
                <v-card-text class="pt-6">
                    <div class="text-subtitle-1 mb-3">{{ $t('Введите заметку') }}</div>

                    <v-textarea
                        v-model="noteDraft"
                        variant="outlined"
                        rows="5"
                        auto-grow
                        :label="$t('Текст заметки')"
                        class="note-textarea"
                        :maxlength="4000"
                        :counter="4000"
                        color="gray"
                    />

                    <div class="d-flex justify-end mt-4">
                        <v-btn variant="outlined" color="gray" class="rounded-pill mr-3 px-6" @click="dialogNote = false">
                            {{ $t('ОТМЕНА') }}
                        </v-btn>

                        <v-btn color="gray" class="rounded-pill px-6" :disabled="!canSaveNote" @click="saveNote">
                            {{ $t('СОХРАНИТЬ') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogReturn" max-width="640">
            <v-card class="note-dialog-card">
                <v-card-text class="pt-6">
                    <div class="text-subtitle-1 mb-3">
                        {{ $t('Причина возврата заявки') }}
                    </div>

                    <v-textarea
                        v-model="returnReasonDraft"
                        variant="outlined"
                        rows="5"
                        auto-grow
                        :label="$t('Укажите причину возврата')"
                        :maxlength="200"
                        :counter="200"
                        color="gray"
                    />

                    <div class="d-flex justify-end mt-4">
                        <v-btn variant="outlined" color="gray" class="rounded-pill mr-3 px-6" @click="closeReturnDialog" :disabled="returnLoading">
                            {{ $t('ОТМЕНА') }}
                        </v-btn>

                        <v-btn color="gray" class="rounded-pill px-6" :loading="returnLoading" :disabled="!canSubmitReturn" @click="confirmReturnRequest">
                            {{ $t('ВЕРНУТЬ') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogExecute" max-width="640">
            <v-card class="note-dialog-card">
                <v-card-text class="pt-6">
                    <div class="text-subtitle-1 mb-3">
                        {{ $t('Комментарий инженера') }}
                    </div>

                    <v-textarea
                        v-model="executeCommentDraft"
                        variant="outlined"
                        rows="5"
                        auto-grow
                        :label="$t('Укажите комментарий')"
                        :maxlength="200"
                        :counter="200"
                        color="gray"
                    />

                    <div class="d-flex justify-end mt-4">
                        <v-btn variant="outlined" color="gray" class="rounded-pill mr-3 px-6" @click="dialogExecute = false" :disabled="executeLoading">
                            {{ $t('ОТМЕНА') }}
                        </v-btn>

                        <v-btn color="gray" class="rounded-pill px-6" :loading="executeLoading" :disabled="!canSubmitExecute" @click="confirmExecuteRequest">
                            {{ $t('ИСПОЛНИТЬ') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogAssignExecutor" max-width="720">
            <v-card class="note-dialog-card">
                <v-card-text class="pt-6">
                    <div class="text-subtitle-1 mb-3">{{ $t('Назначить исполнителя') }}</div>

                    <v-autocomplete
                        v-model="selectedExecutorId"
                        :items="executorUsers"
                        item-value="record_id"
                        :item-text="userTitle"
                        :loading="executorLoading"
                        :disabled="executorLoading"
                        clearable
                        outlined
                        color="gray"
                        :label="$t('Выберите исполнителя')"
                        hide-no-data
                    >
                        <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-bind="attrs" v-on="on">
                                <v-list-item-content>
                                    <v-list-item-title>{{ userTitle(item) }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.login }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </template>

                        <template v-slot:selection="{ item }">
                            <span>{{ userTitle(item) }}</span>
                        </template>
                    </v-autocomplete>

                    <div class="d-flex justify-end mt-4">
                        <v-btn variant="outlined" color="gray" class="rounded-pill mr-3 px-6" @click="closeAssignExecutor" :disabled="executorLoading">
                            {{ $t('ОТМЕНА') }}
                        </v-btn>

                        <v-btn
                            color="gray"
                            class="rounded-pill px-6"
                            :disabled="!selectedExecutorId || executorLoading"
                            :loading="executorLoading"
                            @click="confirmAssignExecutor"
                        >
                            {{ $t('НАЗНАЧИТЬ') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="attachDialog" max-width="720">
            <v-card class="note-dialog-card">
                <v-card-text class="pt-6">
                    <div class="text-subtitle-1 mb-3">{{ $t('Прикрепить документ') }}</div>

                    <div class="text-caption text--secondary mb-2">
                        • {{ $t('Объем прикрепляемого файла не должен превышать 28 Mb.') }}<br />
                        • {{ $t('Допустимы форматы файлов: jpg, jpeg, pdf.') }}
                    </div>

                    <v-file-input
                        v-model="attachFile"
                        outlined
                        dense
                        color="gray"
                        prepend-icon="mdi-paperclip"
                        :label="$t('Файл')"
                        :accept="attachAccept"
                        :error="!!attachFileError"
                        :error-messages="attachFileError ? [attachFileError] : []"
                        @change="validateAttachFile"
                        @click="validateAttachFile"
                    />

                    <div class="d-flex justify-end mt-4">
                        <v-btn variant="outlined" color="gray" class="rounded-pill mr-3 px-6" @click="closeAttachDialog" :disabled="attachLoading">
                            {{ $t('ОТМЕНА') }}
                        </v-btn>

                        <v-btn color="gray" class="rounded-pill px-6" :loading="attachLoading" :disabled="!canAttachSubmit" @click="submitAttachDoc">
                            {{ $t('ПРИКРЕПИТЬ') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-card outlined class="pa-4 mb-6" v-if="sortedStatusHistory.length">
            <div class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t('События заявки') }}
            </div>

            <v-row>
                <v-col cols="12" v-for="s in sortedStatusHistory" :key="s.status_date + '-' + s.status_code_name" class="py-2">
                    <div class="status-row">
                        <div class="status-left">
                            <v-chip
                                :color="getStatusMeta(s.status_code_name).chip"
                                :text-color="getStatusMeta(s.status_code_name).text"
                                outlined
                                small
                                class="font-weight-bold"
                            >
                                <v-icon small left :color="getStatusMeta(s.status_code_name).text">
                                    {{ getStatusMeta(s.status_code_name).icon }}
                                </v-icon>
                                {{ s.status_name___ru }}
                            </v-chip>

                            <span class="text-caption text--secondary mt-1 d-block">
                                {{ formatStatusDate(s.status_date) }}
                            </span>
                        </div>

                        <div v-if="s.comment && String(s.comment).trim()" class="status-note" :class="`status-note--${normalizeStatus(s.status_code_name)}`">
                            <div class="status-note__text">
                                {{ s.comment }}
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <v-card-actions class="justify-end">
            <v-btn color="gray" class="white--text" @click="handleBack" :disabled="uiDisabled || !isDocumentReady || backNavLock" :loading="backNavLock">
                {{ $t('Назад') }}
            </v-btn>
        </v-card-actions>

        <v-overlay :value="loadingreq" opacity="0.08" class="loading-overlay">
            <v-progress-circular indeterminate :size="64" :width="4" color="gray" />
        </v-overlay>
    </v-container>
</template>

<script>
import axios from 'axios';
import prescriptionOZPService from '@/services/apiRequests/prescription_ozp.service';
import { refsService } from '@/services/apiRequests/refs.service';
import { usersService } from '@/services/apiRequests/users.service';

export default {
    components: {},
    name: 'RequestOZPDetail',
    watch: {},
    methods: {
        normRid(v) {
            return (v ?? '')
                .toString()
                .trim()
                .toLowerCase();
        },
        async confirmExecuteRequest() {
            try {
                if (!this.canMenuExecute) return;

                let comment = (this.executeCommentDraft || '').trim();

                const CONTRACT_SUCCESS_TEXT =
                    'Ваш договор продлён. Вам необходимо в течение 5 рабочих дней подписать дополнительное соглашение по адресу: ул. Байзакова, 221, каб. 201.';

                if (this.getRequestGroup() === 'contract') {
                    comment = comment ? `${comment}\n\n${CONTRACT_SUCCESS_TEXT}` : CONTRACT_SUCCESS_TEXT;
                }

                comment = comment.slice(0, 2000);

                if (!comment) return;

                this.executeLoading = true;

                const STATUS_EXECUTED_RID = 4;
                const user_id = this.getCurrentUserId();

                await prescriptionOZPService.reqStatusChange(this.req.record_id, STATUS_EXECUTED_RID, user_id, comment);

                this.$toast?.success?.(this.$t('Заявка исполнена'));

                this.dialogExecute = false;
                await this.loadReq();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось исполнить заявку'));
            } finally {
                this.executeLoading = false;
            }
        },
        getRequestGroup() {
            const code = (this.req?.type_code_name || '')
                .toString()
                .trim()
                .toLowerCase();

            if (code === 'req_pute') return 'pute';
            if (code === 'req_contract') return 'contract';
        },

        getDocDisplayName(doc) {
            const original = (doc?.attach_file_name || '').toString();
            const lower = original.toLowerCase();

            if (lower === 'ozp-request.cms') {
                const num = this.req?.record_id ?? '';

                const dt = doc?.attach_date ? new Date(doc.attach_date) : null;
                const datePart =
                    dt && !isNaN(dt.getTime())
                        ? `${this.pad2(dt.getDate())}.${this.pad2(dt.getMonth() + 1)}.${dt.getFullYear()} ${this.pad2(dt.getHours())}:${this.pad2(
                              dt.getMinutes()
                          )}`
                        : '';

                return `${this.$t('Заявка')} ${num}${datePart ? ' ' + datePart : ''}.pdf`;
            }

            return original;
        },

        getExecutorUserRid(req) {
            return req?.user_rid ?? req?.executor_rid ?? req?.exec_user_rid ?? req?.assigned_user_rid ?? null;
        },

        async loadExecutorUser() {
            const rid = this.executorRid;
            if (!rid) {
                this.executorUser2 = null;
                this.executorName2 = '—';
                return;
            }

            try {
                this.executorLoading2 = true;

                const u = await usersService.getUserInfo(rid);
                this.executorUser2 = u;

                const name = u?.data?.user_data?.name || '';
                this.executorName2 = name ? String(name) : '—';
            } catch (e) {
                console.error('loadExecutorUser error', e);
                this.executorUser2 = null;
                this.executorName2 = '—';
            } finally {
                this.executorLoading2 = false;
            }
        },
        closeAttachDialog() {
            this.attachDialog = false;
            this.attachFile = null;
            this.attachFileError = '';
        },

        validateAttachFile() {
            this.attachFileError = '';

            const f = this.attachFile;
            if (!f) return;

            const maxBytes = 28 * 1024 * 1024; // 28 Mb
            if (f.size > maxBytes) {
                this.attachFileError = this.$t('Файл больше 28 Mb');
                return;
            }

            const name = (f.name || '').toLowerCase();
            const ok = name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.pdf') || name.endsWith('.cms');
            if (!ok) {
                this.attachFileError = this.$t('Недопустимый формат. Разрешено: jpg, jpeg, pdf');
            }
        },

        async submitAttachDoc() {
            try {
                this.validateAttachFile();
                if (!this.canAttachSubmit) return;

                this.attachLoading = true;

                const form = new FormData();
                form.append('file', this.attachFile);

                const qs = new URLSearchParams();

                if (this.req?.guid) qs.set('guid', this.req.guid);

                //qs.set('comment', (this.attachName || '').trim());

                await axios.post(`/prescription_ozp/attachmentsLinkInsert?${qs.toString()}`, form);

                this.$toast?.success?.(this.$t('Документ прикреплён'));

                this.closeAttachDialog();
                await this.loadReq();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось прикрепить документ'));
            } finally {
                this.attachLoading = false;
            }
        },

        userTitle(u) {
            const name = u?.user_data?.name || '';
            const login = u?.login || u?.email || '';
            return name ? `${name}` : login;
        },

        openReturnDialog() {
            if (!this.canMenuReturn) return;
            this.returnReasonDraft = '';
            this.dialogReturn = true;
        },
        closeReturnDialog() {
            this.dialogReturn = false;
            this.returnReasonDraft = '';
        },
        async confirmReturnRequest() {
            try {
                if (!this.canMenuReturn) return;

                const comment = (this.returnReasonDraft || '').trim().slice(0, 200);
                if (!comment) return;

                this.returnLoading = true;

                const STATUS_RETURNED_RID = 5; //потом вместо ридов коды сделаю
                const user_id = this.getCurrentUserId();

                await prescriptionOZPService.reqStatusChange(this.req.record_id, STATUS_RETURNED_RID, user_id, comment);

                this.$toast?.success?.(this.$t('Заявка возвращена на доработку'));

                this.dialogReturn = false;
                await this.loadReq();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось вернуть заявку'));
            } finally {
                this.returnLoading = false;
            }
        },

        openAttachDocs() {
            if (!this.canMenuAttachDocs) return;

            this.attachDialog = true;

            this.attachFile = null;
        },
        openExecuteConfirm() {
            if (!this.canMenuExecute) return;

            this.executeCommentDraft = '';
            this.dialogExecute = true;
        },
        formatDateDDMMYYYY(d = new Date()) {
            const dt = new Date(d);
            const dd = String(dt.getDate()).padStart(2, '0');
            const mm = String(dt.getMonth() + 1).padStart(2, '0');
            const yyyy = dt.getFullYear();
            return `${mm}-${dd}-${yyyy}`;
        },

        getCurrentUserId() {
            return this.$store?.state?.users?.userInfo?.record_id ?? null;
        },

        async assignToMe() {
            try {
                if (!this.canMenuAssignToMe) return;
                if (!this.req?.record_id) return;

                const iRequest_rid = this.req.record_id;
                const user_id = this.getCurrentUserId();
                if (!user_id) {
                    this.$toast?.error?.(this.$t('Не удалось определить текущего пользователя'));
                    return;
                }

                const dDate = this.formatDateDDMMYYYY(new Date());

                await prescriptionOZPService.reqSetExec(iRequest_rid, dDate, user_id);

                await prescriptionOZPService.reqStatusChange(iRequest_rid, 3, user_id);

                this.$toast?.success?.(this.$t('Заявка назначена на вас и переведена в обработку'));
                await this.loadReq();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось назначить заявку'));
            }
        },

        async openAssignExecutor() {
            if (!this.canMenuAssignExecutor) return;

            this.dialogAssignExecutor = true;
            this.selectedExecutorId = null;
            this.executorUsers = [];
            this.executorSearch = '';

            await this.loadExecutorsList();
        },
        async loadRoleRidsForExecutors() {
            const group = this.getRequestGroup();

            const neededNames =
                group === 'pute'
                    ? new Set(['ОР. Назначение заявок на расчет дроссельного устройства', 'ОР. Исполнение заявок на расчет дроссельного устройства'])
                    : new Set(['ОД. Назначение заявок на продление договора', 'ОД. Исполнение заявок на продление договора']);

            if (this.roleRidsCache?.[group]?.length) {
                this.roleRidsForExecutors = this.roleRidsCache[group];
                return this.roleRidsForExecutors;
            }

            const resp = await refsService.getRolesCategoryList(1000, 0);
            const rows = resp?.data || resp || [];

            const rids = rows
                .filter(x => neededNames.has((x?.role_name?.role_name___ru || '').toString().trim()))
                .map(x => x.role_rid)
                .filter(Boolean);

            const unique = Array.from(new Set(rids));

            this.roleRidsCache = this.roleRidsCache || {};
            this.roleRidsCache[group] = unique;

            this.roleRidsForExecutors = unique;
            return unique;
        },

        async loadExecutorsList() {
            try {
                this.executorLoading = true;

                const roleRids = await this.loadRoleRidsForExecutors();
                if (!roleRids.length) {
                    this.executorUsers = [];
                    this.$toast?.error?.(this.$t('Не найдены роли для выбора исполнителей'));
                    return;
                }

                const filter = {
                    locale: '___ru',
                    where: [
                        { field: 'role_rid', value: roleRids },
                        { field: 'active', value: true }
                    ]
                };

                const resp = await usersService.getUserListFilter(10000, 0, filter);
                const list = resp?.data || [];

                this.executorUsers = (list || []).filter(u => u?.record_id).sort((a, b) => this.userTitle(a).localeCompare(this.userTitle(b), 'ru'));
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось загрузить список исполнителей'));
            } finally {
                this.executorLoading = false;
            }
        },
        async confirmAssignExecutor() {
            try {
                if (!this.selectedExecutorId) return;
                if (!this.req?.record_id) return;

                this.executorLoading = true;

                const iRequest_rid = this.req.record_id;
                const dDate = this.formatDateDDMMYYYY(new Date());

                await prescriptionOZPService.reqSetExec(iRequest_rid, dDate, this.selectedExecutorId);

                if (this.req?.status_code_name != 'in_progress') await prescriptionOZPService.reqStatusChange(iRequest_rid, 3, this.getCurrentUserId());

                this.$toast?.success?.(this.$t('Исполнитель назначен'));
                this.dialogAssignExecutor = false;

                await this.loadReq();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось назначить исполнителя'));
            } finally {
                this.executorLoading = false;
            }
        },

        closeAssignExecutor() {
            this.dialogAssignExecutor = false;
            this.selectedExecutorId = null;
        },

        parseInfo(info) {
            try {
                if (!info) return {};
                if (typeof info === 'string') return JSON.parse(info);
                if (typeof info === 'object') return info;
                return {};
            } catch (e) {
                return {};
            }
        },

        getNoteFromInfo(req) {
            const info = this.parseInfo(req?.info);
            return (info?.note || '').toString();
        },

        buildInfoWithNote(req, noteText) {
            const infoObj = this.parseInfo(req?.info);
            return {
                ...infoObj,
                note: (noteText || '').trim()
            };
        },
        isReturnedWithNote(s) {
            return this.normalizeStatus(s?.status_code_name) === 'returned' && !!(s?.comment && String(s.comment).trim());
        },
        normalizeStatus(code) {
            return (code ? String(code) : '').trim().toLowerCase();
        },
        handleBack() {
            if (this.backNavLock) return;
            this.backNavLock = true;
            this.$router.back();

            setTimeout(() => {
                this.backNavLock = false;
            }, 1200);
        },
        getAddInfo(req) {
            const info = this.parseInfo(req?.info);
            return info?.add_info || '';
        },
        getContactPhone(req) {
            const info = this.parseInfo(req?.info);
            return info?.contact_phone || '';
        },
        getContactName(req) {
            const info = this.parseInfo(req?.info);
            return info?.contact_name || '';
        },

        getAttachmentKey(item) {
            return (item && (item.attachment_link_rid || item.attach_id || item.record_id || item.attach_file_name)) || String(Math.random()).slice(2);
        },

        getAttachmentRecordId(item) {
            if (item && (item.attachment_link_rid || item.attach_id || item.record_id)) {
                return item.attachment_link_rid || item.attach_id || item.record_id;
            }

            const list = item && item.attachments;
            if (Array.isArray(list) && list.length === 1) {
                const att = list[0];
                return att.attachment_link_rid || att.attach_id || att.record_id || null;
            }
            return null;
        },

        getAttachmentFileName(item) {
            if (item && item.attach_file_name) return item.attach_file_name;
            const list = item && item.attachments;
            if (Array.isArray(list) && list.length === 1) {
                return list[0].attach_file_name || 'document';
            }
            return 'document';
        },

        openBlobInNewTab({ url, title = 'Документ' }) {
            const w = window.open('', '_blank');
            if (!w) {
                const a = document.createElement('a');
                a.href = url;
                a.target = '_blank';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                a.remove();

                setTimeout(() => URL.revokeObjectURL(url), 60000);
                return;
            }

            try {
                w.opener = null;
            } catch (e) {
                void e;
            }

            w.document.title = title;
            w.document.body.style.margin = '0';
            w.document.body.style.padding = '0';
            const iframe = w.document.createElement('iframe');
            iframe.src = url;
            iframe.style.cssText = 'position:fixed;inset:0;border:0;width:100%;height:100%';
            w.document.body.appendChild(iframe);

            const cleanup = () => setTimeout(() => URL.revokeObjectURL(url), 60000);
            iframe.onload = cleanup;

            setTimeout(cleanup, 90000);
        },
        async previewEventAttachment(item) {
            try {
                const recordId = this.getAttachmentRecordId(item);
                if (!recordId) throw new Error('Не найден идентификатор вложения');

                const { blob, fileName, mime } = await this.fetchAttachmentBlob(recordId);
                const name = fileName || this.getAttachmentFileName(item);

                if (
                    (mime && mime.includes('pdf')) ||
                    String(name)
                        .toLowerCase()
                        .endsWith('.pdf')
                ) {
                    const url = URL.createObjectURL(blob);
                    const displayName = this.getDocDisplayName(item);

                    this.openBlobInNewTab({ url, title: displayName || fileName || 'PDF' });
                } else {
                    await this.downloadEventAttachment(item);
                }
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось открыть документ мероприятия'));
            }
        },

        async downloadEventAttachment(item) {
            try {
                const recordId = this.getAttachmentRecordId(item);
                if (!recordId) throw new Error('Не найден идентификатор вложения');

                const { blob, fileName } = await this.fetchAttachmentBlob(recordId);
                const finalName = fileName || this.getAttachmentFileName(item);

                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = finalName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 60000);
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось скачать документ мероприятия'));
            }
        },
        toYMD(d) {
            const dt = new Date(d);
            const yyyy = dt.getFullYear();
            const mm = String(dt.getMonth() + 1).padStart(2, '0');
            const dd = String(dt.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        },

        getStatusMeta(code) {
            const map = {
                draft: { chip: 'grey lighten-3', text: 'black', icon: 'mdi-file-document-edit-outline' },
                in_queue: { chip: 'blue lighten-5', text: 'blue darken-2', icon: 'mdi-timer-sand' },
                in_progress: { chip: 'blue lighten-5', text: 'blue darken-2', icon: 'mdi-progress-clock' },
                executed: { chip: 'green lighten-5', text: 'green darken-2', icon: 'mdi-check-circle-outline' },
                returned: { chip: 'orange lighten-5', text: 'orange darken-2', icon: 'mdi-arrow-left-circle-outline' },
                canceled: { chip: 'red lighten-5', text: 'red darken-2', icon: 'mdi-cancel' }
            };
            return map[code] || { chip: 'grey lighten-3', text: 'black', icon: 'mdi-help-circle-outline' };
        },
        pad2(n) {
            return String(n).padStart(2, '0');
        },
        formatStatusDate(iso) {
            if (!iso) return '';
            const d = new Date(iso);
            return `${this.pad2(d.getHours())}:${this.pad2(d.getMinutes())} ${this.pad2(d.getDate())}.${this.pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
        },

        async fetchAttachmentBlob(attachId, opts = {}) {
            const resp = await axios.get('/prescription_ozp/getAttachmentFile', {
                params: { id: attachId, unwrapOriginal: opts.unwrapOriginal ? 1 : undefined },
                responseType: 'blob'
            });

            const rawType = (resp.headers && resp.headers['content-type']) || '';
            const disposition = (resp.headers && resp.headers['content-disposition']) || '';
            const filenameMatch = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disposition);
            const fileName = filenameMatch ? decodeURIComponent(filenameMatch[1]) : opts.expectedName || 'document';

            let mime = rawType || 'application/octet-stream';
            const lower = (fileName || '').toLowerCase();
            if ((!mime || mime === 'application/octet-stream') && lower.endsWith('.pdf')) {
                mime = 'application/pdf';
            }

            const typedBlob = new Blob([resp.data], { type: mime });
            return { blob: typedBlob, fileName, mime };
        },
        async previewDoc(doc) {
            try {
                const name = doc?.attach_file_name ? String(doc.attach_file_name) : '';
                const unwrap = name.toLowerCase().endsWith('.cms');
                const { blob, fileName, mime } = await this.fetchAttachmentBlob(doc.record_id, {
                    unwrapOriginal: unwrap,
                    expectedName: name
                });

                if (
                    (mime && mime.includes('pdf')) ||
                    String(fileName)
                        .toLowerCase()
                        .endsWith('.pdf')
                ) {
                    const url = URL.createObjectURL(blob);
                    this.openBlobInNewTab({ url, title: fileName || 'PDF' });
                } else {
                    await this.downloadDoc({ record_id: doc.record_id, attach_file_name: fileName, unwrapOriginal: unwrap });
                }
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось открыть документ'));
            }
        },

        async downloadDoc(doc) {
            try {
                const name = doc?.attach_file_name ? String(doc.attach_file_name) : '';

                const unwrap = !!doc?.unwrapOriginal;
                const { blob, fileName } = await this.fetchAttachmentBlob(doc.record_id, {
                    unwrapOriginal: unwrap,
                    expectedName: name
                });

                const downloadName = this.getDocDisplayName(doc) || fileName || 'document';

                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = downloadName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 60000);
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось скачать документ'));
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            const dd = String(date.getDate()).padStart(2, '0');
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const yyyy = date.getFullYear();
            return `${dd}.${mm}.${yyyy}`;
        },
        async loadReq() {
            const id = this.$route.params.id;
            this.loadingreq = true; // одинаковое имя
            try {
                const response = await this.$store.dispatch('prescription_ozp/getReqOZPInfo', id);
                this.req = response;
                await this.loadExecutorUser();
            } catch (error) {
                console.error('Ошибка при загрузке заявки:', error);
                this.$toast?.error?.(this.$t('Ошибка при загрузке данных'));
            } finally {
                this.loadingreq = false;
            }
        },

        openNoteDialog() {
            this.noteDraft = this.getNoteFromInfo(this.req);
            this.dialogNote = true;
        },
        async saveNote() {
            try {
                const base = this.req || {};
                const infoObj = this.buildInfoWithNote(base, this.noteDraft);

                const typeCode = base.type_code_name;
                const requestTypeRid = base.request_type_rid ?? null;

                const oc = Array.isArray(base.object_contracts) && base.object_contracts.length ? base.object_contracts[0] : null;

                const objectRid = oc?.object_rid ?? base.object_rid ?? null;

                const contractOrMeterRid = oc?.contract_or_meter_rid ?? null;

                const userId = this.$store?.state?.auth?.user?.guid || this.$store?.state?.auth?.user?.user_id || base.user_id || null;

                const processedModel = {
                    record_id: base.record_id,
                    request_type_rid: requestTypeRid,
                    request_date: base.request_date,
                    info: infoObj,
                    user_id: userId,
                    to_delete: 0,

                    contract_or_meter_rid: contractOrMeterRid,
                    object_rid: objectRid
                };

                if (!processedModel.request_type_rid) throw new Error('request_type_rid пустой');
                if (!processedModel.contract_or_meter_rid) throw new Error('contract_or_meter_rid пустой (не найден contract_rid/meter_rid)');
                if (!processedModel.object_rid) throw new Error('object_rid пустой (не найден object_rid)');

                await this.$store.dispatch('prescription_ozp/postReqOZP', {
                    nvcPrescriptionRequestData: processedModel
                });

                this.dialogNote = false;
                this.$toast?.success?.(this.$t('Заметка сохранена'));
                await this.loadReq();
            } catch (e) {
                console.error(e);
                this.$toast?.error?.(this.$t('Не удалось сохранить заметку'));
            }
        }
    },
    data() {
        return {
            dialogExecute: false,
            executeCommentDraft: '',
            executeLoading: false,
            roleRidsCache: {},
            executorLoading2: false,
            executorUser2: null,
            executorName2: '—',
            req: null,
            loadingreq: false,
            dialogNote: false,
            noteDraft: '',
            actionLoading: false,
            edits: {},
            backNavLock: false,
            dialogReturn: false,
            returnReasonDraft: '',
            returnLoading: false,
            dialogAssignExecutor: false,
            executorLoading: false,
            executorUsers: [],
            executorSearch: '',
            selectedExecutorId: null,
            roleRidsForExecutors: [],
            attachDialog: false,
            attachFile: null,
            attachLoading: false,
            attachFileError: '',
            attachAccept: '.jpg,.jpeg,.pdf,.cms'
        };
    },
    async mounted() {
        this.loadReq();
        this.expandedSections = [0, 1, 2];
    },
    computed: {
        canSubmitExecute() {
            const len = (this.executeCommentDraft || '').trim().length;
            return len > 0 && len <= 200 && !this.executeLoading;
        },
        canAttachSubmit() {
            return !this.attachLoading && !!this.attachFile && !this.attachFileError;
        },
        executorRid() {
            return this.req?.user_rid ?? this.req?.executor_rid ?? this.req?.exec_user_rid ?? this.req?.assigned_user_rid ?? null;
        },
        isAssigned() {
            return !!this.getExecutorUserRid(this.req);
        },
        currentUserRid() {
            const u = this.$store?.state?.users?.userInfo;
            return u?.guid || u?.user_rid || u?.user_id || u?.record_id || null;
        },

        isCurrentUserExecutor() {
            const execRid = this.executorRid;
            return !!this.normRid(execRid) && this.normRid(execRid) === this.normRid(this.currentUserRid);
        },

        canSubmitReturn() {
            const len = (this.returnReasonDraft || '').trim().length;
            return len > 0 && len <= 200 && !this.returnLoading;
        },

        canAssignByRole() {
            const translated = require('@/utils/translate.util').default(this.$store?.state?.users?.userInfo, 'full', this.$i18n?.locale || 'ru');
            const roleText = String(translated?.role_name?.role_name || '');

            const r1 = roleText.includes('ОД. Назначение заявок на продление договора');
            const r3 = roleText.includes('ОР. Назначение заявок на расчет дроссельного устройства');

            return r1 || r3;
        },

        isInQueueStatus() {
            const code = (this.req?.status_code_name || '')
                .toString()
                .trim()
                .toLowerCase();
            return code === 'in_queue';
        },

        statusCode() {
            return (this.req?.status_code_name || '')
                .toString()
                .trim()
                .toLowerCase();
        },

        isInQueue() {
            return this.statusCode === 'in_queue';
        },

        isInProgress() {
            return this.statusCode === 'in_progress';
        },

        canShowActionsMenu() {
            return this.hasAnyMenuAction && this.isDocumentReady && !this.uiDisabled;
        },

        canShowButComment() {
            const { r5  } = this.rolesFlags;

            return this.hasAnyMenuAction && this.isDocumentReady && !this.uiDisabled && !r5;
        },

        rolesFlags() {
            const translated = require('@/utils/translate.util').default(this.$store?.state?.users?.userInfo, 'full', this.$i18n?.locale || 'ru');
            const roleText = String(translated?.role_name?.role_name || '');

            const r1 = roleText.includes('ОД. Назначение заявок на продление договора');
            const r2 = roleText.includes('ОД. Исполнение заявок на продление договора');
            const r3 = roleText.includes('ОР. Назначение заявок на расчет дроссельного устройства');
            const r4 = roleText.includes('ОР. Исполнение заявок на расчет дроссельного устройства');
            const r5 = roleText.includes('Администратор ОЗП') || roleText.includes('Администратор. AHS') || roleText.includes('Әкімші. AHS');

            return { r1, r2, r3, r4, r5 };
        },
        canButComment() {
            const { r5 } = this.rolesFlags;
            return r5;
        },
        canMenuAssignToMe() {
            const { r1, r3 } = this.rolesFlags;
            return this.isInQueue && !this.isAssigned && (r1 || r3);
        },
        canMenuAssignExecutor() {
            const { r1, r3 } = this.rolesFlags;
            return (this.isInQueue || this.isInProgress) && (r1 || r3);
        },
        canMenuReturn() {
            return this.isInProgress && this.isCurrentUserExecutor;
        },
        canMenuAttachDocs() {
            return this.isInProgress && this.isCurrentUserExecutor;
        },
        canMenuExecute() {
            return this.isInProgress && this.isCurrentUserExecutor;
        },
        hasAnyMenuAction() {
            return this.canMenuAssignToMe || this.canMenuAssignExecutor || this.canMenuReturn || this.canMenuAttachDocs || this.canMenuExecute;
        },
        factDateErrorText() {
            const fmt = ymd => {
                if (!ymd) return '';
                const [y, m, d] = ymd.split('-');
                return `${d}.${m}.${y}`;
            };
            return `Дата должна быть в диапазоне ${fmt(this.factMinDate)} — ${fmt(this.factMaxDate)}`;
        },
        isDocumentReady() {
            return !!(this.req && this.req.record_id);
        },
        uiDisabled() {
            return this.loadingreq || this.actionLoading;
        },

        sortedStatusHistory() {
            const arr = this.req?.status_history || [];

            return arr.slice().sort((a, b) => new Date(a.status_date) - new Date(b.status_date));
        },

        hasDocuments() {
            return Array.isArray(this.req?.req_documents) && this.req.req_documents.length > 0;
        },
        canSaveNote() {
            const current = (this.getNoteFromInfo(this.req) || '').trim();
            const draft = (this.noteDraft || '').trim();
            return draft.length > 0 && draft !== current;
        }
    }
};
</script>

<style scoped>
.col-docs {
    min-width: 240px;
}

.events-table {
    table-layout: fixed;
    width: 100%;
}
.col-name {
    width: 50%;
}
.col-plan {
    width: 12%;
}
.col-fact {
    width: 14%;
}
.col-docs {
    width: 20%;
}
.col-actions {
    width: 80px;
    text-align: center;
}
.col-actions--org {
    text-align: right;
}

.docs-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.doc-row {
    display: flex;
    flex-direction: column;
}
.doc-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.doc-actions {
    display: flex;
    gap: 8px;
    margin-left: 26px;
    margin-top: 2px;
}

@media (max-width: 1440px) {
    .col-docs {
        min-width: 320px;
    }
}

.signed-hint {
    background: #f6c343;
    color: #000;
    border-radius: 4px;
    padding: 10px 12px;
}
.signed-hint__icon {
    width: 20px;
    margin-right: 8px;
}
.signed-hint__text {
    line-height: 1.4;
}

.note-dialog-card {
    border-radius: 12px;
}
.note-textarea :deep(.v-field) {
    border-radius: 10px;
}
:deep(.v-btn.rounded-pill) {
    text-transform: uppercase;
}

.boxed-section {
    position: relative;
    border: 1px solid rgba(25, 118, 210, 0.5);
    border-radius: 12px;
}
.section-title {
    position: absolute;
    top: -10px;
    left: 14px;
    padding: 0 8px;
    background: white;
    color: #1976d2;
    font-weight: 600;
    font-size: 14px;
}
.note-fab {
    position: absolute;
    top: -14px;
    right: 12px;
    background: #6b7bb2;
    color: white;
}

:deep(.v-simple-table tbody td) {
    vertical-align: middle;
}
.actions-cell,
.actions-cell--org {
    text-align: right;
    vertical-align: middle;
    padding-right: 0;
    white-space: nowrap;
    min-width: 72px;
}
.col-actions--org {
    text-align: center;
}
.actions-wrap {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
}
.actions-wrap .v-btn {
    min-width: auto;
}
.actions-cell .v-icon {
    display: inline-flex;
    vertical-align: middle;
}

.group-header {
    font-size: 18px;
    font-weight: 400;
}

.status-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}
.status-left {
    min-width: 140px;
}
.status-note {
    position: relative;
    flex: 0 0 auto;
    display: inline-block;
    width: fit-content;
    max-width: 70%;
    border-radius: 10px;
    padding: 10px 12px;
    white-space: pre-wrap;
    word-break: break-word;
}

.status-note--returned {
    background: #fff7e6;
    border: 1px solid #f5c06a;
}
.status-note__time {
    position: absolute;
    right: 10px;
    top: 6px;
    font-size: 12px;
    color: #9e9e9e;
}
.status-note__text {
    color: #5d4037;
    line-height: 1.4;
}

.row-locked {
    outline: 2px solid #f6c343;
    background: #fff8e1;
    transition: background 0.2s ease;
}
.req-addinfo {
    white-space: normal;

    overflow-wrap: anywhere;
    word-break: break-word;

    max-width: 100%;
    overflow: hidden;
}
.status-note--executed {
    background: #e8f5e9;
    border: 1px solid #81c784;
}

.status-note--returned {
    background: #fff7e6;
    border: 1px solid #f5c06a;
}

.status-note--in_progress {
    background: #e3f2fd;
    border: 1px solid #64b5f6;
}

.status-note--canceled {
    background: #ffebee;
    border: 1px solid #e57373;
}
</style>
